import { Inject, Injectable } from "@angular/core";
import { EntityResponse, isEntityResponse, WsACKResponse } from "@common/api/ack";
import { ID } from "@common/interfaces/id";
import { FilterType, Topics } from '@common/interfaces/topics';
import { AppService } from "@ep-om/app.service";
import { AuthService } from "@ep-om/project/auth/auth.service";
import { logger } from "@ep-om/utils/logger";
import { BehaviorSubject, combineLatest, Observable, Subject } from "rxjs";
import { debounceTime, distinctUntilChanged, filter, map, repeatWhen, takeUntil } from "rxjs/operators";
import { Manager, Socket } from 'socket.io-client';
import { RealtimeEngineService } from '../../realtime-v2/realtime-engine.service';


//export type FilterType<T> = { [P in keyof T]?: T[P] } | (() => { [P in keyof T]?: T[P] });


@Injectable({
  providedIn: 'root',
})
export class SocketIoService {
  private manager: Manager = new Manager(`${this.address}`, {
    autoConnect: false,
    transports: ['websocket'],
    //extraHeaders: { token: this.authService.authQuery.getValue().accessToken },
    reconnection: false,
    query: {
      token: this.authService.authQuery.getValue().accessToken,
    }
  });
  public socket: Socket;
  public sockets: { [key in Topics]?: Socket } = {};
  public connected$ = new BehaviorSubject<boolean>(false);
  private _disconnected$ = new Subject<void>();
  private _reconnect$ = new Subject<boolean>();
  private legacyRealtimeEnabled = false;

  constructor(
    @Inject('SOCKET_IO_ADDRESS') private address: string,
    private appService: AppService,
    private authService: AuthService,
    private engine: RealtimeEngineService,
  ) {

    // Il socket legacy v1 è abilitato solo quando l'arbitro dice 'v1' (e c'è un token). NON deriva
    // più da user$: l'arbitro è l'unica fonte di verità e il suo engine$ è stabile, quindi niente
    // più connect/disconnect ad ogni riemissione (potenzialmente stantia) dell'utente.
    combineLatest([
      this.authService.authQuery.accessToken$,
      this.engine.engine$,
    ]).pipe(
      map(([token, currentEngine]) => ({
        token,
        enabled: !!token && currentEngine === 'v1',
      })),
      distinctUntilChanged((previous, current) => (
        previous.token === current.token &&
        previous.enabled === current.enabled
      )),
    ).subscribe(({ enabled }) => {
      this.legacyRealtimeEnabled = enabled;

      if (!enabled) {
        this.disconnect();
        return;
      }

      this.init();
    });

    /// Need to reconnect while offline
    this._reconnect$.pipe(
      debounceTime(5000),
    ).subscribe(() => {
      if (this.authService.isLoggedIn() && this.legacyRealtimeEnabled) {
        this.connect();
      }
    });

  }

  public uponConnection$ = this.connected$.pipe(
    debounceTime(300),
    filter(x => x === true),
  );

  public stopOnDisconnectionPipe<T>() {
    return takeUntil<T>(this._disconnected$);
  }

  public repeatOnConnectionPipe<T>() {
    return repeatWhen<T>(() => this.connected$.pipe(filter(x => x === false)));
  }

  listenEntity<T>(topic: Topics, filters?: FilterType): Observable<{ topic: Topics, data: T[] }> {
    return new Observable<{ topic: Topics, data: T[] }>(observer => {
      this.sockets[topic].on('entity_updates', (data: EntityResponse<T[]>|T[]) => {
        const _isEntityResponse = isEntityResponse(data);
        if (!filters || !filters.entity || (_isEntityResponse && data.entityName === filters.entity)) {
          observer.next({ topic, data: _isEntityResponse ? data.entities : data });
        }
      });
      this.sockets[topic].emit(`entity_subscribe`, { filters });
      return () => {
        let resource = 'entity';
        if (!!filters && filters.entity) {
          resource += `_${filters.entity}`;
        }
        this.sockets[topic].emit(`entity_unsubscribe`, resource);
        this.sockets[topic].off(`entity_updates`);
      }
    })
  }

  listenResource<T>(topic: Topics, resource: string, filters?: any): Observable<{topic: Topics, data: T}> {
    return new Observable<{topic: Topics, data: T}>(observer => {
      const listener = (data: T) => observer.next({ topic, data });
      this.sockets[topic].on(`${resource}_updates`, listener);
      this.sockets[topic].emit(`${resource}_subscribe`, { filters });
      return () => {
        this.sockets[topic].emit(`${resource}_unsubscribe`, filters ? { filters } : resource);
        this.sockets[topic].off(`${resource}_updates`, listener);
      }
    })
  }

  sendRequest<T>(topic: Topics, resource: string, body: any): Promise<WsACKResponse<T>> {
    return new Promise((resolve, reject) => {
      this.sockets[topic].emit(resource, body, response => {
        resolve(response)
      });
    });
  }

  remove(topic: Topics, id: ID) {
    this.socket.emit(`${topic}.remove`, id);
  }

  init() {
    if (!this.legacyRealtimeEnabled) {
      return;
    }
    if (this.socket)
      this.connect()
    else
      this.socketSetup();
  }

  connect() {
    if (!this.legacyRealtimeEnabled) {
      return;
    }
    if (!this.socket) {
      this.socketSetup();
      return;
    }
    if (!this.socket.disconnected) {
      return;
    }
    this.manager.opts = {
      ...this.manager.opts,
      query: { token: this.authService.authQuery.getValue().accessToken },
      //extraHeaders: { token: this.authService.authQuery.getValue().accessToken },
    }
    this.socket.open();
  }

  disconnect() {
    if (!this.socket) {
      this.connected$.next(false);
      return;
    }
    console.log('[SOCKET-IO] - Forcing disconnection');
    this.socket.disconnect();
    Object.values(this.sockets).forEach(s => s.disconnect());
    this._disconnected$.next();
    this.connected$.next(false);
  }

  socketSetup() {

    Object.values(Topics).forEach(topic => {
      this.sockets[topic] = this.manager.socket(`/${topic}`)
      this.sockets[topic].on('connect_error', (message) => {
        console.log('[SOCKET] - error trying to connect to socket', topic, message);
      });
    });

    this.socket = this.manager.socket('/');

    this.socket.on('connect', async () => {
      console.log("[SOCKET] - CONNECTED")
      await this.appService.check();
      Object.values(this.sockets).forEach(socket => socket.connect());
      this.connected$.next(true)
    });
    this.socket.on('reconnect', async () => {
      await this.appService.check();
    })
    this.socket.on('disconnect', async () => {
      console.log("[SOCKET] - DISCONNECTED")
      this.appService.setOffline();
      Object.values(this.sockets).forEach(socket => socket.off())
      this._disconnected$.next();
      this.connected$.next(false);
      this._reconnect$.next(true);
      this.appService.updateMaintab(false);
    });
    this.socket.on('reconnecting_attempt', () => { logger.log('trying to reconnect...') });
    this.socket.on('connect_error', async (message) => {
      console.log("SOCKET CONNECT ERROR", message.name, message.message, message);
      const accessToken = this.authService.authQuery.getAccessToken();
      const expires = accessToken ? this.authService.getExpirationFromToken(accessToken) : new Date('1995-12-17T03:24:00');
      let timeout = expires.getTime() - Date.now() - (10 * 1000) / 1000;
      const amIOnline = await this.appService.amIOnline();
      if (
        amIOnline === true &&
        !this.appService.mainTab$.value &&
        timeout < 5
      ) {
        // Auto-elezione coordinata cross-tab: una sola tab del browser diventa main
        // (Web Locks), evitando refresh concorrenti da più tab.
        const becameMain = await this.appService.claimMainTabLeadership();
        if (becameMain) {
          console.log('[SOCKET] - I am the main tab (leader-elected)');
        }
      }
      this._reconnect$.next(true);

    });

    this.connect();
  }
}
